import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IndexeddbPersistence } from "y-indexeddb";
import * as Y from "yjs";

import { rpc } from "../api/rpc";
import type { AuthInfo } from "../api/types";
import { colorForUser } from "../editor/collab";
import type { CollaborationProviderLike, CollaborationUser } from "../editor/types";
import type { Connector, SessionEvent, SessionHandle } from "./connection";
import { connectHocuspocus } from "./connection";
import {
  CLOSE_CODES,
  SCROLL_THROTTLE_MS,
  initialSessionState,
  isEditable,
  localStoreName,
  peersFrom,
  statusOf,
  throttle,
  type CollaborationPeer,
  type CollaborationStatus,
  type SessionState,
  type Throttled,
} from "./session";

/** Mirrors the shared document into IndexedDB so offline edits survive a refresh (§15). */
export interface LocalStore {
  destroy: () => Promise<void>;
}
export type LocalStoreFactory = (name: string, ydoc: Y.Doc) => LocalStore;

export interface UseCollaborationOptions {
  /** Null while the screen does not know which document it is showing yet. */
  documentId: string | null;
  user: { id: string; name: string } | null;
  /** The token to open with. Omitted, the hook fetches one from `auth.info`. */
  token?: string;
  /** Off for a share link, a revision preview, or wherever collaboration is unavailable. */
  enabled?: boolean;
  /** Seams. Production passes none of these; tests pass all of them. */
  connector?: Connector;
  localStore?: LocalStoreFactory | null;
  fetchToken?: () => Promise<string>;
  origin?: string;
}

export interface Collaboration {
  ydoc: Y.Doc | null;
  provider: CollaborationProviderLike | null;
  status: CollaborationStatus;
  peers: CollaborationPeer[];
  /** False when the browser refused local persistence, which blocks offline editing. */
  hasLocalPersistence: boolean;
  /** Whether the editor should accept input right now. */
  editable: boolean;
  /** The local user's identity as the editor's awareness binding wants it. */
  user: CollaborationUser | null;
  /** Publishes the local scroll offset in viewport heights, throttled per §10. */
  publishScroll: (viewportHeights: number) => void;
}

/** `auth.info` issues collaboration tokens only to session-authenticated callers (§2). */
export async function fetchCollaborationToken(): Promise<string> {
  const result = await rpc<AuthInfo>("auth.info");
  const token = result.data.collaborationToken;
  if (token === undefined || token === "") {
    throw new Error("This session cannot join a collaborative document");
  }
  return token;
}

function defaultLocalStore(): LocalStoreFactory | null {
  if (typeof indexedDB === "undefined") {
    return null;
  }
  return (name, ydoc) => new IndexeddbPersistence(name, ydoc);
}

/**
 * State that belongs to one document. Stamping it rather than resetting it on every change of
 * document is what keeps this hook free of cascading renders: a value left over from the
 * previous document is simply not read.
 */
interface StampedState extends SessionState {
  documentId: string | null;
}

interface StampedPeers {
  documentId: string | null;
  list: CollaborationPeer[];
}

const NO_PEERS: CollaborationPeer[] = [];

/**
 * One live collaborative session for one document: a Yjs replica, its IndexedDB mirror, the
 * socket, and the presence state that hangs off it (`docs/spec/collaboration.md` §10, §15).
 *
 * The hook owns no editing logic. It hands `{ydoc, provider, user}` to `<Editor>`, which binds
 * them with `y-prosemirror`, and it reports a `status` the surrounding screen can render.
 */
export function useCollaboration(options: UseCollaborationOptions): Collaboration {
  const { documentId, user, token, enabled = true } = options;
  const connector = options.connector ?? connectHocuspocus;
  const active = enabled && documentId !== null && user !== null;

  const [stamped, setStamped] = useState<StampedState>({
    ...initialSessionState,
    documentId: null,
  });
  const [peerState, setPeerState] = useState<StampedPeers>({ documentId: null, list: [] });
  const [storageDeniedFor, setStorageDeniedFor] = useState<string | null>(null);
  const [session, setSession] = useState<SessionHandle | null>(null);
  const sessionRef = useRef<SessionHandle | null>(null);

  // A new document is a new replica: reusing one would merge two documents into each other.
  const ydoc = useMemo(() => (active ? new Y.Doc() : null), [active, documentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Held in a ref rather than in the session effect's dependencies: a caller that passes an
  // inline function would otherwise tear the socket down on every render.
  const fetchToken = options.fetchToken ?? fetchCollaborationToken;
  const fetchTokenRef = useRef(fetchToken);
  useEffect(() => {
    fetchTokenRef.current = fetchToken;
  }, [fetchToken]);

  // Keyed on the fields rather than the object, because a caller that builds `user` inline
  // would otherwise hand us a new identity on every render and tear the socket down with it.
  const userId = user?.id ?? null;
  const userName = user?.name ?? null;
  const identity: CollaborationUser | null = useMemo(
    () =>
      userId === null ? null : { id: userId, name: userName ?? "", color: colorForUser(userId) },
    [userId, userName],
  );

  const storeFactory = options.localStore === undefined ? defaultLocalStore() : options.localStore;
  const factoryRef = useRef(storeFactory);
  useEffect(() => {
    factoryRef.current = storeFactory;
  }, [storeFactory]);
  const canPersistLocally = storeFactory !== null;

  useEffect(() => {
    if (!active || ydoc === null || documentId === null || identity === null) {
      return;
    }
    let cancelled = false;
    let handle: SessionHandle | null = null;
    let store: LocalStore | null = null;

    /** Writes into this document's slice, discarding anything left from a previous one. */
    const patch = (change: Partial<SessionState>): void => {
      setStamped((current) => ({
        ...(current.documentId === documentId ? current : { ...initialSessionState, documentId }),
        ...change,
        documentId,
      }));
    };

    // Opening the mirror is asynchronous in every implementation, so its failure is reported
    // the same way: private browsing, denied storage or an exhausted quota leave the session
    // without local persistence, and §15 then has the caller block offline editing rather
    // than accumulate changes it cannot keep.
    void Promise.resolve().then(() => {
      if (cancelled) {
        return;
      }
      const factory = factoryRef.current;
      if (factory === null) {
        return;
      }
      try {
        store = factory(localStoreName(documentId), ydoc);
      } catch {
        setStorageDeniedFor(documentId);
      }
    });

    const onEvent = (event: SessionEvent): void => {
      if (cancelled) {
        return;
      }
      switch (event.type) {
        case "status":
          patch({ socket: event.status });
          break;
        case "authenticated":
          patch({ scope: event.scope });
          break;
        case "close":
          setStamped((current) => {
            const base =
              current.documentId === documentId ? current : { ...initialSessionState, documentId };
            return {
              ...base,
              documentId,
              lastCloseCode: event.code,
              reloadRequired:
                base.reloadRequired || event.code === CLOSE_CODES.editorUpdateRequired,
              // A reconnect re-authenticates from scratch, so the granted scope is unknown.
              scope: null,
            };
          });
          if (event.code === CLOSE_CODES.editorUpdateRequired) {
            handle?.stop();
          }
          break;
        case "awareness":
          setPeerState({
            documentId,
            list: peersFrom(event.states, handle?.clientId() ?? null),
          });
          break;
      }
    };

    const start = async (): Promise<void> => {
      let first: string;
      try {
        first = token ?? (await fetchTokenRef.current());
      } catch {
        if (!cancelled) {
          patch({ socket: "disconnected", lastCloseCode: CLOSE_CODES.authenticationFailed });
        }
        return;
      }
      if (cancelled) {
        return;
      }
      handle = connector({
        documentId,
        ydoc,
        token: first,
        // Every reconnect presents a freshly minted token, so a 24-hour expiry never strands
        // a tab that has been open longer than that (§15).
        getToken: async () => {
          const next = await fetchTokenRef.current();
          handle?.refreshToken(next);
          return next;
        },
        onEvent,
        ...(options.origin === undefined ? {} : { origin: options.origin }),
      });
      handle.setField("user", identity);
      sessionRef.current = handle;
      setSession(handle);
    };
    void start();

    return () => {
      cancelled = true;
      handle?.destroy();
      sessionRef.current = null;
      setSession(null);
      void store?.destroy();
    };
    // `connector` and `options.origin` are configuration, fixed for the life of a screen;
    // re-running on their identity would tear the socket down on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, documentId, identity, token, ydoc]);

  // Built inside an effect rather than in a memo: the throttle closes over the session ref,
  // and creating it during render would mean reading a ref while rendering.
  const publishRef = useRef<Throttled<number> | null>(null);
  useEffect(() => {
    const publish = throttle((value: number) => {
      sessionRef.current?.setField("scrollY", value);
    }, SCROLL_THROTTLE_MS);
    publishRef.current = publish;
    return () => {
      publish.cancel();
      publishRef.current = null;
    };
  }, []);

  const publishScroll = useCallback((value: number) => {
    publishRef.current?.(value);
  }, []);

  const live: SessionState =
    stamped.documentId === documentId ? stamped : { ...initialSessionState };
  const status = statusOf(live);
  const hasLocalPersistence = canPersistLocally && storageDeniedFor !== documentId;

  return {
    ydoc,
    provider: session?.provider ?? null,
    status,
    peers: peerState.documentId === documentId ? peerState.list : NO_PEERS,
    hasLocalPersistence,
    editable: isEditable(status, hasLocalPersistence),
    user: identity,
    publishScroll,
  };
}
